import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import { Checkbox } from "../../../../components/ui/checkbox";
import { useAudit } from "../../../../contexts/auditContext";
import { sessionSpecName } from "../../../../lib/sessionSpecName";

interface RegenerateWithInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (instructions: string, persistToControl: boolean) => Promise<void>;
  title: string;
  description?: string;
}

export const RegenerateWithInstructionsDialog = ({
  open,
  onOpenChange,
  onSubmit,
  title,
  description,
}: RegenerateWithInstructionsDialogProps) => {
  // The instruction is written back into whichever spec the session runs —
  // the control, or the work step. Read from the provider rather than taken as
  // a prop: the dialog has four call sites in this view, and a missed one would
  // name the wrong spec.
  const { sessionKind } = useAudit();
  const specName = sessionSpecName(sessionKind);
  const [instructions, setInstructions] = useState("");
  const [persistToControl, setPersistToControl] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset on every open, not just on Cancel/submit: ESC, overlay, and X close
  // through onOpenChange without passing our handlers, and a leaked checked
  // checkbox would silently bake the next rerun's instructions into the control.
  useEffect(() => {
    if (open) {
      setInstructions("");
      setPersistToControl(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!instructions.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(instructions, persistToControl);
      setInstructions("");
      setPersistToControl(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting instructions:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setInstructions("");
    setPersistToControl(false);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Allow Shift+Enter for new line, but submit on just Enter
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="instructions">Custom Instructions</Label>
          <Textarea
            id="instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Focus on the invoice date format, check for matching PO numbers, ensure amounts are in USD..."
            rows={4}
            disabled={isSubmitting}
          />
          <p className="text-xs text-stone-500">
            Press Enter to submit, Shift+Enter for new line
          </p>
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="persist-to-control"
            checked={persistToControl}
            onCheckedChange={(checked) => setPersistToControl(checked === true)}
            disabled={isSubmitting}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="persist-to-control" className="cursor-pointer">
              Also update the {specName}
            </Label>
            <p className="text-xs text-stone-500">
              This checkbox enables the Midship agent to integrate your
              instructions back into the {specName}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!instructions.trim() || isSubmitting}
          >
            {isSubmitting ? "Regenerating..." : "Regenerate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
